import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  CalendarRange,
  TrendingDown,
  Landmark,
} from 'lucide-react';
import { Card, Button, MoneyInput, Select, InputGroup } from '../../components/v2/V2Components';
import MortgageService from '../../services/MortgageService';
import HouseService from '../../services/HouseService';
import InsuranceService from '../../services/InsuranceService';
import usePageView from '../../hooks/usePageView';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';
import { buildAmortizationSchedule } from '../../utils/amortization';
import {
  amortizationDefaultsFrom,
  formatCycle,
  formatCycleStart,
  latestDisclosure,
} from '../../utils/escrowDisclosures';

const formatCurrency = (value, { dash = true } = {}) => {
  if (value == null || value === '') return dash ? '—' : '$0.00';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num)) return dash ? '—' : '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

const parseLocalDate = (d) => {
  if (!d) return null;
  const date = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(`${d}T00:00:00`) : new Date(d);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatMonthYear = (d) => {
  const date = parseLocalDate(d);
  if (!date) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

// Given a YYYY-MM (or YYYY-MM-DD) string, return the first of the PRIOR month as
// YYYY-MM-01. Used to label the statement-comparison period when the statement
// is the very first payment (no earlier schedule row to borrow a date from).
const monthBefore = (ym) => {
  if (!ym) return null;
  const [y, mo] = String(ym).slice(0, 7).split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(mo)) return null;
  const d = new Date(Date.UTC(y, mo - 2, 1));
  const yy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${yy}-${mm}-01`;
};

// Parse a MoneyInput value (it emits formatted strings like "1,200.50") into a
// number, falling back to 0 for blank/invalid input.
const moneyToNumber = (v) => {
  if (v == null || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v.replace(/[^0-9.-]/g, '')) : v;
  return Number.isFinite(n) ? n : 0;
};

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// The tax year a given YYYY-MM(-DD) row falls in, given the tax-year start month
// (1 = January). For a July (7) start, Jan–Jun 2026 belongs to tax year 2025.
const taxYearFor = (dateStr, startMonth) => {
  if (!dateStr) return null;
  const [y, mo] = String(dateStr).slice(0, 7).split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(mo)) return null;
  return mo >= startMonth ? y : y - 1;
};

/**
 * A labelled figure used in the summary grid.
 *
 * `min-w-0` lets the enclosing grid track shrink below the min-content width of
 * an unbreakable currency string (a grid item's automatic minimum size is
 * clamped by its own min-width); `break-words` gives the string a break point.
 * Without both, the two-column mobile grid overflows its card.
 */
const Stat = ({ label, value, accent = false }) => (
  <div className="min-w-0">
    <div className="text-[var(--text-muted)] text-xs uppercase tracking-wide break-words">{label}</div>
    <div
      className={`font-semibold break-words ${accent ? 'text-[var(--primary)]' : 'text-[var(--text-main)]'}`}
    >
      {value}
    </div>
  </div>
);

// Collapse the per-month rows into one row per calendar year (or per 12-payment
// block when the loan has no origination date to anchor real dates).
const toAnnualRows = (rows) => {
  const groups = new Map();
  rows.forEach((r) => {
    const key = r.date ? r.date.slice(0, 4) : `Year ${Math.ceil(r.number / 12)}`;
    const g = groups.get(key) || { key, principal: 0, interest: 0, total: 0, endBalance: 0 };
    g.principal += r.principal;
    g.interest += r.interest;
    g.total += r.payment;
    g.endBalance = r.balance; // last row in the group wins (rows are ordered)
    groups.set(key, g);
  });
  return Array.from(groups.values());
};

const MortgageAmortization = () => {
  usePageView(ANALYTICS_EVENTS.MORTGAGE_AMORTIZATION_VIEWED);
  const { houseId, mortgageId } = useParams();
  const navigate = useNavigate();

  const [mortgage, setMortgage] = useState(null);
  const [latestStatement, setLatestStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [extraMonthlyPrincipal, setExtraMonthlyPrincipal] = useState('');
  const [view, setView] = useState('annual');

  // Escrow breakdown (page-local what-if — not persisted, except PMI which is
  // seeded from the loan). Defaults are auto-pulled from the house's tax records,
  // the Insurance tab's latest policy, and the property HOA fee.
  const [taxYearly, setTaxYearly] = useState('');     // default yearly property tax
  const [insuranceYearly, setInsuranceYearly] = useState('');
  const [hoaMonthly, setHoaMonthly] = useState('');
  const [pmiMonthly, setPmiMonthly] = useState('');
  const [taxYearStartMonth, setTaxYearStartMonth] = useState(1); // 1 = January
  const [taxOverrides, setTaxOverrides] = useState({}); // { [year]: 'amount' }
  const [showTaxByYear, setShowTaxByYear] = useState(false);
  const [statementEscrow, setStatementEscrow] = useState(null); // latest statement's reported escrow, for reference
  const [escrowDisclosure, setEscrowDisclosure] = useState(null); // latest escrow disclosure, when one seeded the defaults

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        // The mortgage is required; everything else is best-effort — statements
        // feed the reconciliation card, and escrow disclosures, house and
        // insurance feed the escrow breakdown defaults. None of them may block
        // the schedule (which needs only the loan terms).
        const [mortgageRes, statementsRes, houseRes, insuranceRes, disclosuresRes] = await Promise.allSettled([
          MortgageService.getMortgage(mortgageId),
          MortgageService.getStatements(mortgageId),
          houseId ? HouseService.getHouse(houseId) : Promise.resolve(null),
          houseId ? InsuranceService.getHousePolicies(houseId) : Promise.resolve(null),
          MortgageService.getEscrowDisclosures(mortgageId),
        ]);
        if (cancelled) return;
        if (mortgageRes.status !== 'fulfilled') throw mortgageRes.reason;
        const m = mortgageRes.value;
        const statementsData = statementsRes.status === 'fulfilled' ? statementsRes.value : null;
        const house = houseRes.status === 'fulfilled' ? houseRes.value : null;
        const insuranceData = insuranceRes.status === 'fulfilled' ? insuranceRes.value : null;
        const disclosuresData = disclosuresRes.status === 'fulfilled' ? disclosuresRes.value : null;
        // Guard against URL tampering: kick the user to the right house.
        if (houseId && String(m.house_id) !== String(houseId)) {
          navigate(`/houses/${m.house_id}/mortgages/${m.id}/amortization`, { replace: true });
          return;
        }
        setMortgage(m);
        // Statements come back ordered due_date DESC, so index 0 is the latest.
        const latest = statementsData?.statements?.[0] || null;
        setLatestStatement(latest);
        setStatementEscrow(latest && latest.escrow != null && latest.escrow !== '' ? Number(latest.escrow) : null);

        // --- Seed the escrow-breakdown defaults from existing data. Always assign
        // (even to '') so switching mortgages/houses clears a prior one's values. ---

        // The servicer's latest escrow disclosure is what the escrow payment is
        // actually set from, so each of its figures wins over the records below;
        // a figure it doesn't carry falls back field by field (ADR-0013).
        const disclosure = latestDisclosure(disclosuresData?.disclosures || []);
        const fromDisclosure = amortizationDefaultsFrom(disclosure);
        setEscrowDisclosure(disclosure);

        // Taxes: per-year records fill the override map for the years they
        // cover. The default for every other year is the disclosure's projected
        // taxes, else the most recent record, else the house's denormalized
        // "last property tax".
        const taxRecords = Array.isArray(house?.tax_records) ? house.tax_records : [];
        const overrides = {};
        taxRecords.forEach((rec) => {
          if (rec && rec.year != null && rec.total_amount != null) {
            overrides[rec.year] = String(rec.total_amount);
          }
        });
        setTaxOverrides(overrides);
        const latestTaxRecord = taxRecords
          .filter((r) => r && r.total_amount != null && r.year != null)
          .sort((a, b) => b.year - a.year)[0];
        const defaultTax =
          fromDisclosure.taxYearly ?? latestTaxRecord?.total_amount ?? house?.last_property_tax_amount ?? null;
        setTaxYearly(defaultTax != null ? String(defaultTax) : '');

        // Insurance: latest policy (by start_date) yearly premium.
        const policies = Array.isArray(insuranceData?.policies)
          ? insuranceData.policies
          : Array.isArray(insuranceData)
            ? insuranceData
            : [];
        const latestPolicy = policies
          .filter((p) => p && p.total_amount != null)
          .sort((a, b) => String(b.start_date || '').localeCompare(String(a.start_date || '')))[0];
        const defaultInsurance = fromDisclosure.insuranceYearly ?? latestPolicy?.total_amount ?? null;
        setInsuranceYearly(defaultInsurance != null ? String(defaultInsurance) : '');

        // HOA: monthly fee from property records.
        setHoaMonthly(house?.hoa?.monthly_fee != null ? String(house.hoa.monthly_fee) : '');

        // PMI: the disclosure's mortgage insurance ÷ 12, else the loan's own figure.
        const defaultPmi = fromDisclosure.pmiMonthly ?? m.pmi_monthly ?? null;
        setPmiMonthly(defaultPmi != null ? String(defaultPmi) : '');

        setTaxYearStartMonth(1);
        setLoadError(null);
      } catch (err) {
        if (cancelled) return;
        if (err.code === 404) setLoadError('Mortgage not found.');
        else if (err.code === 401) setLoadError('Authentication required.');
        else setLoadError(err.message || 'Failed to load mortgage.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mortgageId, houseId, navigate]);

  const extraNum = moneyToNumber(extraMonthlyPrincipal);

  const insuranceMonthly = moneyToNumber(insuranceYearly) / 12;
  const hoaNum = moneyToNumber(hoaMonthly);
  const pmiNum = moneyToNumber(pmiMonthly);
  const defaultTaxYearly = moneyToNumber(taxYearly);

  // Which escrow components are non-zero — drives which columns the monthly table
  // shows (so a loan with only taxes+insurance doesn't render empty PMI/HOA cols).
  const anyTaxOverride = Object.values(taxOverrides).some((v) => moneyToNumber(v) > 0);
  const activeComponents = useMemo(
    () => ({
      tax: defaultTaxYearly > 0 || anyTaxOverride,
      insurance: insuranceMonthly > 0,
      pmi: pmiNum > 0,
      hoa: hoaNum > 0,
    }),
    [defaultTaxYearly, anyTaxOverride, insuranceMonthly, pmiNum, hoaNum],
  );

  // Per-row escrow resolver: taxes vary by tax-year (with per-year overrides),
  // insurance is the yearly premium ÷12, HOA and PMI are flat monthly figures.
  const escrowResolver = useMemo(() => {
    return (dateStr) => {
      const year = taxYearFor(dateStr, taxYearStartMonth);
      const override = year != null ? taxOverrides[year] : undefined;
      const yearlyTax = override != null && override !== '' ? moneyToNumber(override) : defaultTaxYearly;
      return {
        tax: yearlyTax / 12,
        insurance: insuranceMonthly,
        pmi: pmiNum,
        hoa: hoaNum,
      };
    };
  }, [taxOverrides, taxYearStartMonth, defaultTaxYearly, insuranceMonthly, pmiNum, hoaNum]);

  // The live schedule (reflects the escrow breakdown + extra-payment what-ifs).
  const schedule = useMemo(() => {
    if (!mortgage) return null;
    return buildAmortizationSchedule({
      originalLoanAmount: mortgage.original_loan_amount,
      annualRatePct: mortgage.interest_rate,
      termMonths: mortgage.term_months,
      originationDate: mortgage.origination_date,
      extraMonthlyPrincipal: extraNum,
      escrowResolver,
    });
  }, [mortgage, extraNum, escrowResolver]);

  // A stable baseline from the original terms (no extra payments) used to
  // reconcile the forecast against the balance the user's statement reports.
  const baselineSchedule = useMemo(() => {
    if (!mortgage) return null;
    return buildAmortizationSchedule({
      originalLoanAmount: mortgage.original_loan_amount,
      annualRatePct: mortgage.interest_rate,
      termMonths: mortgage.term_months,
      originationDate: mortgage.origination_date,
    });
  }, [mortgage]);

  // Compare the schedule's computed balance for the latest statement's period
  // against the actual balance the lender reported. The two should land close
  // when the loan is tracking its original terms.
  const reconciliation = useMemo(() => {
    if (!baselineSchedule || !mortgage) return null;
    const actual = mortgage.current_principal_balance;
    const periodDate = latestStatement?.due_date || mortgage.next_due_date;
    if (actual == null || !periodDate) return null;
    const ym = String(periodDate).slice(0, 7);
    // The statement date is the payment DUE date, and each schedule row's balance
    // is the balance AFTER that month's payment is applied. The balance the lender
    // reports is as of the statement — i.e. BEFORE the due month's payment — which
    // equals the PRIOR row's ending balance. So a statement due Jun 2026 must be
    // compared against the May 2026 computed balance, not June's. When the statement
    // is the very first payment, there is no prior row: compare against the opening
    // loan balance.
    const dueIdx = baselineSchedule.rows.findIndex((r) => r.date && r.date.slice(0, 7) === ym);
    if (dueIdx === -1) return null;
    const priorRow = dueIdx > 0 ? baselineSchedule.rows[dueIdx - 1] : null;
    const computed = priorRow ? priorRow.balance : Number(mortgage.original_loan_amount);
    if (!Number.isFinite(computed)) return null;
    const comparePeriod = priorRow ? priorRow.date : monthBefore(ym);
    const diff = Number(actual) - computed;
    return { computed, actual: Number(actual), diff, comparePeriod };
  }, [baselineSchedule, latestStatement, mortgage]);

  // The distinct tax years the schedule spans, in order — the rows of the
  // "Set taxes by year" editor. Empty when the loan has no origination date
  // (no real calendar dates to bucket by).
  const taxYears = useMemo(() => {
    if (!schedule) return [];
    const seen = new Set();
    const years = [];
    schedule.rows.forEach((r) => {
      const y = taxYearFor(r.date, taxYearStartMonth);
      if (y != null && !seen.has(y)) {
        seen.add(y);
        years.push(y);
      }
    });
    return years;
  }, [schedule, taxYearStartMonth]);

  const setTaxOverride = (year, value) =>
    setTaxOverrides((prev) => ({ ...prev, [year]: value }));

  if (loading) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Card className="p-10 flex items-center justify-center text-[var(--text-muted)]">
          <Loader2 className="animate-spin mr-2" size={18} />
          Loading amortization…
        </Card>
      </div>
    );
  }

  const backToDetail = () => navigate(`/houses/${houseId}/mortgages/${mortgageId}`);

  if (loadError || !mortgage) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Button leadingIcon={ArrowLeft} variant="ghost" onClick={backToDetail}>
          Back
        </Button>
        <Card className="p-10 text-center mt-4">
          <AlertCircle size={32} className="mx-auto mb-3 text-[var(--error)]" />
          <p className="text-[var(--text-main)]">{loadError || 'Mortgage not found.'}</p>
        </Card>
      </div>
    );
  }

  const summary = schedule?.summary;
  const annualRows = schedule ? toAnnualRows(schedule.rows) : [];

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      <button
        onClick={backToDetail}
        className="inline-flex items-center gap-1 text-sm text-[var(--text-secondary)] hover:text-[var(--text-main)] mb-4"
      >
        <ArrowLeft size={16} /> Back to mortgage
      </button>

      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-xl bg-[var(--bg-hover)] text-[var(--primary)]">
          <CalendarRange size={28} />
        </div>
        <div>
          <h1 className="font-headline text-2xl font-bold text-[var(--text-main)]">
            Amortization schedule
          </h1>
          <div className="text-sm text-[var(--text-secondary)]">
            {mortgage.lender}
            {mortgage.interest_rate != null && (
              <span className="text-[var(--text-muted)]"> · {mortgage.interest_rate}% · {mortgage.term_months ? `${mortgage.term_months} mo` : '—'}</span>
            )}
          </div>
        </div>
      </div>

      {!schedule ? (
        <Card className="p-10 text-center">
          <Landmark size={32} className="mx-auto mb-3 text-[var(--text-muted)]" />
          <p className="text-[var(--text-main)] font-medium mb-1">
            Not enough loan details to build a schedule.
          </p>
          <p className="text-[var(--text-secondary)] text-sm mb-5 max-w-md mx-auto">
            An amortization schedule needs the original loan amount, the interest rate, and the
            term (in months). Add those to this mortgage to see your full payment breakdown.
          </p>
          <Button
            onClick={() => navigate(`/houses/${houseId}/mortgages/${mortgageId}/edit`)}
          >
            Edit loan details
          </Button>
        </Card>
      ) : (
        <>
          {/* Escrow breakdown */}
          <Card className="p-5 mb-6">
            <h2 className="text-sm font-bold text-[var(--text-main)] uppercase tracking-wide mb-1">
              Escrow breakdown
            </h2>
            <p className="text-xs text-[var(--text-muted)] mb-4">
              Taxes, insurance, HOA and PMI are added to your principal &amp; interest to show the
              full monthly cost.{' '}
              {escrowDisclosure
                ? `Defaults come from your escrow disclosure for ${formatCycle(escrowDisclosure.cycle_start)}, then your property records and Insurance tab`
                : 'Defaults are pulled from your property records and Insurance tab'}{' '}
              — edit them here to explore what-ifs (changes here aren&apos;t saved).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputGroup label="Yearly property tax">
                <MoneyInput
                  name="tax_yearly"
                  value={taxYearly}
                  onChange={(e) => setTaxYearly(e.target.value)}
                />
              </InputGroup>
              <InputGroup label="Yearly insurance">
                <MoneyInput
                  name="insurance_yearly"
                  value={insuranceYearly}
                  onChange={(e) => setInsuranceYearly(e.target.value)}
                />
              </InputGroup>
              <InputGroup label="Monthly HOA">
                <MoneyInput
                  name="hoa_monthly"
                  value={hoaMonthly}
                  onChange={(e) => setHoaMonthly(e.target.value)}
                />
              </InputGroup>
              <InputGroup label="Monthly PMI">
                <MoneyInput
                  name="pmi_monthly"
                  value={pmiMonthly}
                  onChange={(e) => setPmiMonthly(e.target.value)}
                />
              </InputGroup>
              <InputGroup label="Tax year starts in">
                <Select
                  name="tax_year_start"
                  value={String(taxYearStartMonth)}
                  onChange={(e) => setTaxYearStartMonth(Number(e.target.value))}
                >
                  {MONTHS.map((name, idx) => (
                    <option key={name} value={String(idx + 1)}>
                      {name}
                    </option>
                  ))}
                </Select>
              </InputGroup>
              <InputGroup label="Extra monthly principal (what-if)">
                <MoneyInput
                  name="extra"
                  value={extraMonthlyPrincipal}
                  onChange={(e) => setExtraMonthlyPrincipal(e.target.value)}
                />
              </InputGroup>
            </div>

            {escrowDisclosure?.new_escrow_payment != null && (
              <p className="text-xs text-[var(--text-muted)] mt-3">
                Your servicer set escrow at {formatCurrency(escrowDisclosure.new_escrow_payment)}/mo starting{' '}
                {formatCycleStart(escrowDisclosure.cycle_start)}.
              </p>
            )}
            {statementEscrow != null && (
              <p className="text-xs text-[var(--text-muted)] mt-3">
                Your latest statement reported {formatCurrency(statementEscrow)} of monthly escrow.
              </p>
            )}

            {/* Per-year tax overrides */}
            {taxYears.length > 0 && (
              <div className="mt-4 border-t border-[var(--border)] pt-4">
                <button
                  type="button"
                  onClick={() => setShowTaxByYear((v) => !v)}
                  className="text-sm font-medium text-[var(--primary)] hover:underline"
                  aria-expanded={showTaxByYear}
                >
                  {showTaxByYear ? 'Hide' : 'Set taxes by year'}
                </button>
                {showTaxByYear && (
                  <>
                    <p className="text-xs text-[var(--text-muted)] mt-2 mb-3">
                      Override the property tax for individual tax years (
                      {taxYearStartMonth === 1
                        ? 'calendar year'
                        : `${MONTHS[taxYearStartMonth - 1]}–${MONTHS[(taxYearStartMonth + 10) % 12]} tax year`}
                      ). Blank years use the yearly property tax above.
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-72 overflow-y-auto pr-1">
                      {taxYears.map((year) => (
                        <InputGroup key={year} label={String(year)}>
                          <MoneyInput
                            name={`tax_${year}`}
                            value={taxOverrides[year] ?? ''}
                            placeholder={defaultTaxYearly > 0 ? formatCurrency(defaultTaxYearly) : ''}
                            onChange={(e) => setTaxOverride(year, e.target.value)}
                          />
                        </InputGroup>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </Card>

          {/* Summary */}
          <Card className="p-5 mb-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5 text-sm">
              <Stat label="Monthly principal & interest" value={formatCurrency(summary.monthlyPI)} />
              <Stat label="Monthly escrow" value={formatCurrency(summary.escrowMonthly, { dash: false })} />
              <Stat label="Total monthly payment" value={formatCurrency(summary.totalMonthly)} accent />
              <Stat label="Payoff date" value={formatMonthYear(summary.payoffDate)} />
              <Stat
                label="Payments remaining"
                value={`${summary.monthsToPayoff} mo`}
              />
              <Stat label="Total interest over life" value={formatCurrency(summary.totalInterest)} />
            </div>

            {summary.interestSaved != null && summary.interestSaved > 0 && (
              <div className="mt-5 flex items-start gap-2 rounded-lg bg-[var(--bg-hover)] p-3">
                <TrendingDown size={18} className="text-[var(--primary)] mt-0.5 shrink-0" />
                <div className="text-sm text-[var(--text-main)]">
                  Paying {formatCurrency(extraNum)} extra each month saves{' '}
                  <span className="font-semibold text-[var(--primary)]">
                    {formatCurrency(summary.interestSaved)}
                  </span>{' '}
                  in interest and pays the loan off{' '}
                  <span className="font-semibold">
                    {summary.monthsSaved} {summary.monthsSaved === 1 ? 'month' : 'months'}
                  </span>{' '}
                  earlier.
                </div>
              </div>
            )}
          </Card>

          {/* Balance reconciliation against the lender's reported balance */}
          {reconciliation && (
            <Card className="p-5 mb-6">
              <h2 className="text-sm font-bold text-[var(--text-main)] uppercase tracking-wide mb-3">
                Compared to your latest statement
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-5 text-sm">
                <Stat
                  label={`Computed balance (${formatMonthYear(reconciliation.comparePeriod)})`}
                  value={formatCurrency(reconciliation.computed)}
                />
                <Stat label="Actual balance on statement" value={formatCurrency(reconciliation.actual)} />
                <Stat
                  label="Difference"
                  value={formatCurrency(reconciliation.diff)}
                  accent={Math.abs(reconciliation.diff) > 1000}
                />
              </div>
              <p className="text-xs text-[var(--text-muted)] mt-3">
                {Math.abs(reconciliation.diff) <= 1000
                  ? 'Your loan is tracking close to its original schedule.'
                  : 'These differ by more than expected — you may be ahead from extra payments, or the loan terms (rate/amount/date) may need updating.'}
              </p>
            </Card>
          )}

          {/* Schedule table */}
          <Card className="p-5">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
              <h2 className="text-sm font-bold text-[var(--text-main)] uppercase tracking-wide">
                Schedule
              </h2>
              <div className="w-full sm:w-48">
                <Select value={view} onChange={(e) => setView(e.target.value)} aria-label="Schedule view">
                  <option value="annual">Yearly summary</option>
                  <option value="monthly">Month by month</option>
                </Select>
              </div>
            </div>

            <div className="overflow-x-auto">
              {view === 'annual' ? (
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-xs uppercase text-[var(--text-secondary)] border-b border-[var(--border)]">
                      <th className="py-2 px-2 font-semibold">Year</th>
                      <th className="py-2 px-2 font-semibold text-right">Principal</th>
                      <th className="py-2 px-2 font-semibold text-right">Interest</th>
                      <th className="py-2 px-2 font-semibold text-right">Total P&amp;I</th>
                      <th className="py-2 px-2 font-semibold text-right">Ending balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {annualRows.map((g) => (
                      <tr key={g.key} className="border-b border-[var(--border)] last:border-0">
                        <td className="py-2.5 px-2 text-sm font-medium text-[var(--text-main)]">{g.key}</td>
                        <td className="py-2.5 px-2 text-sm text-right text-[var(--text-secondary)]">{formatCurrency(g.principal)}</td>
                        <td className="py-2.5 px-2 text-sm text-right text-[var(--text-secondary)]">{formatCurrency(g.interest)}</td>
                        <td className="py-2.5 px-2 text-sm text-right text-[var(--text-main)]">{formatCurrency(g.total)}</td>
                        <td className="py-2.5 px-2 text-sm text-right font-semibold text-[var(--text-main)]">{formatCurrency(g.endBalance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-xs uppercase text-[var(--text-secondary)] border-b border-[var(--border)]">
                      <th className="py-2 px-2 font-semibold">#</th>
                      <th className="py-2 px-2 font-semibold">Date</th>
                      <th className="py-2 px-2 font-semibold text-right">Principal</th>
                      <th className="py-2 px-2 font-semibold text-right">Interest</th>
                      <th className="py-2 px-2 font-semibold text-right">P&amp;I</th>
                      {activeComponents.tax && <th className="py-2 px-2 font-semibold text-right">Tax</th>}
                      {activeComponents.insurance && <th className="py-2 px-2 font-semibold text-right">Insurance</th>}
                      {activeComponents.pmi && <th className="py-2 px-2 font-semibold text-right">PMI</th>}
                      {activeComponents.hoa && <th className="py-2 px-2 font-semibold text-right">HOA</th>}
                      <th className="py-2 px-2 font-semibold text-right">Total</th>
                      <th className="py-2 px-2 font-semibold text-right">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.rows.map((r) => (
                      <tr key={r.number} className="border-b border-[var(--border)] last:border-0">
                        <td className="py-2 px-2 text-sm text-[var(--text-muted)]">{r.number}</td>
                        <td className="py-2 px-2 text-sm text-[var(--text-main)]">{formatMonthYear(r.date)}</td>
                        <td className="py-2 px-2 text-sm text-right text-[var(--text-secondary)]">{formatCurrency(r.principal)}</td>
                        <td className="py-2 px-2 text-sm text-right text-[var(--text-secondary)]">{formatCurrency(r.interest)}</td>
                        <td className="py-2 px-2 text-sm text-right text-[var(--text-main)]">{formatCurrency(r.payment)}</td>
                        {activeComponents.tax && (
                          <td className="py-2 px-2 text-sm text-right text-[var(--text-secondary)]">{formatCurrency(r.escrowBreakdown?.tax, { dash: false })}</td>
                        )}
                        {activeComponents.insurance && (
                          <td className="py-2 px-2 text-sm text-right text-[var(--text-secondary)]">{formatCurrency(r.escrowBreakdown?.insurance, { dash: false })}</td>
                        )}
                        {activeComponents.pmi && (
                          <td className="py-2 px-2 text-sm text-right text-[var(--text-secondary)]">{formatCurrency(r.escrowBreakdown?.pmi, { dash: false })}</td>
                        )}
                        {activeComponents.hoa && (
                          <td className="py-2 px-2 text-sm text-right text-[var(--text-secondary)]">{formatCurrency(r.escrowBreakdown?.hoa, { dash: false })}</td>
                        )}
                        <td className="py-2 px-2 text-sm text-right text-[var(--text-main)]">{formatCurrency(r.totalMonthly)}</td>
                        <td className="py-2 px-2 text-sm text-right font-semibold text-[var(--text-main)]">{formatCurrency(r.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default MortgageAmortization;
